/**
 * AddEditTappingPointsV2 — V2 port of the Tapping Point edit form.
 *
 * Sections:
 *   01 Tapping point info — name (required, locked when is_default
 *      because the default row's name is referenced by other code)
 *   02 Image              — UploadTile with the avatar-composite
 *      hover preview that the v1 form uses (transparent PNG of
 *      the dot markers, optionally composited over the avatar
 *      illustration on hover so admins can see how the app
 *      renders it).
 *   03 Danger zone        — only when the row isn't the default
 *      one (default tapping point is a fixture).
 *
 * Same Redux fetch / submit graph as v1:
 *   • fetchTappingPoints → state.tapping_points.tapping_points
 *   • addEditTappingPoint(JSON.stringify(payload), navigate)
 *   • deleteTappingPoint(id, navigate)
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

import LoadingBackdrop from '../../Common/LoadingBackdrop';
import {
  fetchTappingPoints,
  addEditTappingPoint,
  deleteTappingPoint,
} from '../../../actions/tappingPointsActions';
import { uploadImage, clearMedia } from '../../../actions/mediaActions';
import { preventEnterFormSubmission } from '../../../utils/formUtils';

import { Button } from '../../redesign';
import {
  FormSection,
  FieldBlock,
  TextInput,
  UploadTile,
} from '../../redesign/edit';
import PreviewCardTP from './PreviewCardTP';

const AddEditTappingPointsV2 = forwardRef(function AddEditTappingPointsV2(
  { isEdit, selectedId, onLoaded },
  ref,
) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tapping_points = useSelector((s) => s.tapping_points?.tapping_points);
  const image = useSelector((s) => s.media?.image);
  const isFetching = useSelector((s) => s.tapping_points?.isLoading);

  const [state, setState] = useState({
    tapping_point_name: '',
    tapping_point_image_url: '',
    tapping_point_is_default: 0,
    isSetToEdit: false,
  });

  const [isSaving, setIsSaving] = useState(false);

  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  /* ─── Fetch on mount (1:1 with v1) ─────────────────────────── */
  useEffect(() => {
    if (!isEdit || isFetching) return;
    dispatch(fetchTappingPoints());
  }, [dispatch, isEdit, isFetching]);

  /* Hydrate state once the collection lands in Redux. */
  useEffect(() => {
    if (!isEdit || !tapping_points) return;
    const found = tapping_points.find(
      (p) => p.tapping_point_id === Number(selectedId),
    );
    if (!found) {
      // Match v1's "couldn't find it" warning
      Swal.fire({ icon: 'warning', text: 'Error finding selected Tapping Point' });
      return;
    }
    setState((prev) => {
      if (prev.isSetToEdit) return prev;
      return { ...found, isSetToEdit: true };
    });
    if (onLoadedRef.current) {
      onLoadedRef.current({
        tapping_point_name: found.tapping_point_name,
        tapping_point_updated_at:
          found.tapping_point_updated_at ?? found.updated_at ?? null,
      });
    }
  }, [isEdit, tapping_points, selectedId]);

  /* Update image URL once the upload settles in Redux media. */
  useEffect(() => {
    if (image) {
      setState((prev) => ({ ...prev, tapping_point_image_url: image }));
    }
  }, [image]);

  /* ─── Handlers ─────────────────────────────────────────────── */
  const handleImageUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    dispatch(uploadImage(file));
  }, [dispatch]);

  const handleRemoveImage = () => {
    setState((prev) => ({ ...prev, tapping_point_image_url: '' }));
    dispatch(clearMedia());
  };

  const submit = useCallback(() => {
    if (!state.tapping_point_name) {
      Swal.fire('Error', 'Please enter tapping point name', 'error');
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        tapping_point_name: state.tapping_point_name,
        tapping_point_image_url: state.tapping_point_image_url,
        tapping_point_is_default: state.tapping_point_is_default,
        ...(isEdit && { tapping_point_id: selectedId }),
      };
      const result = dispatch(addEditTappingPoint(
        JSON.stringify(payload),
        () => navigate('/tapping-points'),
      ));
      if (result && typeof result.finally === 'function') {
        result.finally(() => setIsSaving(false));
      } else {
        setTimeout(() => setIsSaving(false), 3000);
      }
    } catch (error) {
      console.error('Error saving tapping point:', error);
      Swal.fire('Error', 'Failed to save tapping point', 'error');
      setIsSaving(false);
    }
  }, [state, isEdit, selectedId, dispatch, navigate]);

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    submit();
  };

  useImperativeHandle(ref, () => ({ submit }), [submit]);

  /* ─── Delete ─────────────────────────────────────────────── */
  const handleDelete = () => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteTappingPoint(selectedId, () => navigate('/tapping-points')));
      }
    });
  };

  if (isEdit && !tapping_points) {
    return <LoadingBackdrop open />;
  }

  return (
    <>
      <LoadingBackdrop open={isSaving} />
      <div
        className="edit-canvas grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] items-start"
        style={{ gap: 24 }}
      >
      <form
        id="tapping-point-form"
        onSubmit={handleSubmit}
        onKeyDown={preventEnterFormSubmission}
        autoComplete="off"
        style={{ minWidth: 0 }}
      >
        {/* 01 Tapping point info */}
        <FormSection
          title="Tapping point info"
          num="01"
          right={
            state.tapping_point_is_default ? (
              <span
                className="font-condensed font-bold uppercase"
                style={{
                  fontSize: 10,
                  letterSpacing: '0.1em',
                  padding: '3px 9px',
                  borderRadius: 999,
                  background: '#1AA6B714',
                  color: '#1A8A98',
                }}
                title="The default row is a system fixture — its name is locked."
              >
                Default
              </span>
            ) : null
          }
        >
          <FieldBlock
            label="Name"
            required
            hint={
              state.tapping_point_is_default
                ? "Locked — this row is the default fallback used when a session doesn't pick one"
                : undefined
            }
            htmlFor="f-tp-name"
            noMargin
          >
            <TextInput
              id="f-tp-name"
              name="tapping_point_name"
              value={state.tapping_point_name}
              onChange={(e) =>
                setState((prev) => ({ ...prev, tapping_point_name: e.target.value }))
              }
              disabled={!!state.tapping_point_is_default}
              required
            />
          </FieldBlock>
        </FormSection>

        {/* 02 Image — the live composite preview now lives in the
            rail's PreviewCardTP, so the form's tile can stay a
            plain UploadTile. */}
        <FormSection
          title="Image"
          num="02"
          right={
            <span className="text-text-muted" style={{ fontSize: 11.5 }}>
              Transparent PNG · composited over the avatar at runtime
            </span>
          }
        >
          <div style={{ maxWidth: 320 }}>
            <UploadTile
              label=""
              value={state.tapping_point_image_url}
              accept="image/*"
              onChange={handleImageUpload}
              onRemove={handleRemoveImage}
              emptyText="Click to upload the dot-marker PNG"
            />
          </div>
        </FormSection>

        {/* 03 Danger zone — hidden for the default row */}
        {isEdit && !state.tapping_point_is_default && (
          <FormSection title="Danger zone" num="03">
            <div className="flex items-center" style={{ gap: 12, flexWrap: 'wrap' }}>
              <Button variant="danger" onClick={handleDelete} type="button">
                Delete tapping point
              </Button>
              <span className="text-text-muted" style={{ fontSize: 12 }}>
                Permanent — sessions referencing this point will fall back to the default.
              </span>
            </div>
          </FormSection>
        )}
      </form>

      {/* Right rail — live composite preview that updates as the
          user edits the name + uploads a new dot-marker PNG. */}
      <aside
        className="edit-rail"
        style={{
          alignSelf: 'start',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          minWidth: 0,
        }}
      >
        <PreviewCardTP state={state} />
      </aside>
      </div>
    </>
  );
});

export default AddEditTappingPointsV2;
